import { __decorate } from "tslib";
import { localized, msg, str } from '@lit/localize';
import HelixElement from '../../shared/types/helix-element.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { css, html, nothing } from 'lit';
import { addDays, addMonths, format, getDaysInMonth, isAfter, isBefore, isSameDay, isValid, parse, startOfMonth, startOfWeek, } from 'date-fns';
import * as DateFnsLocales from 'date-fns/locale';
import { UTCDate } from '@date-fns/utc';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import { classMap } from 'lit/directives/class-map.js';
import { watch } from '../../shared/directives/watch.decorator.js';
import { localizationConfig } from '../localization-provider/localization-provider.js';
import { createPopper } from '@popperjs/core';
import '../text-field/text-field.js';
import '../icon/icon.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { findForm } from '../../shared/forms/form-utils.js';
let HlxDatePicker = class HlxDatePicker extends HelixElement {
    constructor() {
        super();
        this.viewState = 'hidden';
        this.value = undefined;
        this.type = 'single';
        this.disabled = false;
        this.name = '';
        this.placeholder = this.getLocalizedDateFormat();
        this.required = false;
        this.helpText = '';
        this.label = '';
        const dateToDisplay = this.getDateToDisplay();
        this.displayedYear = dateToDisplay.getFullYear();
        this.displayedMonth = dateToDisplay.getMonth();
    }
    render() {
        const date = new Date(this.displayedYear, this.displayedMonth);
        return html `
            <hlx-text-field
                class="hlx-date-picker-control"
                name=${ifDefined(this.name)}
                placeholder=${ifDefined(this.placeholder)}
                title=${this.title}
                label=${this.label}
                help-text=${this.helpText}
                ?required=${this.required}
                ?disabled=${this.disabled}
                @input=${this.handleInput}
                @blur=${this.handleBlur}>
                <button
                    slot="suffix"
                    ?disabled=${this.disabled}
                    @click=${this.togglePopup}
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-open-date-picker-button': true,
        })}>
                    <hlx-icon name="calendar" title="Calendar icon" />
                </button>
            </hlx-text-field>
            <div
                class=${classMap({
            'hlx-date-picker-container': true,
            'hlx-animation-with-prefers-reduced-motion': true,
        })}
                ?data-hide=${this.viewState === 'hidden'}
                ?data-show=${this.viewState !== 'hidden'}>
                <div class="hlx-date-picker-header">
                    <div>
                        <button
                            @click=${this.toggleSelectingMonthView}
                            class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-dropdown': true,
        })}>
                            ${date.toLocaleDateString(localizationConfig.getLocale(), {
            month: 'long',
        })}
                        </button>
                        <button
                            @click=${this.toggleSelectingYearView}
                            class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-dropdown': true,
        })}>
                            ${this.displayedYear}
                        </button>
                    </div>
                    ${this.renderMonthNavigation()}
                </div>
                ${this.renderCurrentView()}
            </div>
        `;
    }
    handleViewStateChange() {
        this.popper?.update();
    }
    firstUpdated(_changedProperties) {
        this.popper = createPopper(this.input, this.datePickerContainer, {
            placement: 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
        });
    }
    renderMonthNavigation() {
        if (this.viewState !== 'selecting_date') {
            return nothing;
        }
        const firstDayOfDisplayedMonth = new UTCDate(this.displayedYear, this.displayedMonth);
        const prevMonth = addDays(firstDayOfDisplayedMonth, -1);
        const nextMonth = addMonths(firstDayOfDisplayedMonth, 1);
        const isMin = this.min && isBefore(prevMonth, this.min);
        const isMax = this.max && isAfter(nextMonth, this.max);
        return html `
            <div>
                <button
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-month-navigation-button': true,
        })}
                    ?disabled=${isMin}
                    @click=${this.decrementDisplayedMonth}>
                    <hlx-icon name="chevron-left" title="Previous month icon" />
                </button>
                <button
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-month-navigation-button': true,
        })}
                    ?disabled=${isMax}
                    @click=${this.incrementDisplayedMonth}>
                    <hlx-icon name="chevron-right" title="Next month icon" />
                </button>
            </div>
        `;
    }
    renderCurrentView() {
        switch (this.viewState) {
            case 'selecting_month':
                return this.renderMonthSelector();
            case 'selecting_year':
                return this.renderYearSelector();
            default:
                return this.renderDateSelector();
        }
    }
    renderMonthSelector() {
        const months = [...Array(12).keys()].map((monthIndex) => new UTCDate(this.displayedYear, monthIndex));
        const minMonth = this.min ? startOfMonth(this.min) : null;
        const maxMonth = this.max ? startOfMonth(this.max) : null;
        const monthButtons = months.map((month) => html `
                <button
                    ?disabled=${(minMonth && isBefore(month, minMonth)) ||
            (maxMonth && isAfter(month, maxMonth))}
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-options-button': true,
            'hlx-selected': month.getMonth() === this.displayedMonth,
        })}
                    @click=${() => {
            this.displayedMonth = month.getMonth();
            this.viewState = 'selecting_date';
        }}>
                    ${month.toLocaleDateString(localizationConfig.getLocale(), { month: 'short' })}
                </button>
            `);
        return html `
            <div class="hlx-options-grid">${monthButtons}</div>
        `;
    }
    renderYearSelector() {
        const currentYear = new Date().getFullYear();
        // todo: unclear how many years should be available by default, should it be an infinite scroll?
        const minYear = this.min ? this.min.getFullYear() : currentYear - 10;
        const maxYear = this.max ? this.max.getFullYear() : currentYear + 10;
        const span = maxYear - minYear + 1;
        const years = [...Array(span).keys()].map((yearIndex) => minYear + yearIndex);
        const yearButtons = years.map((year) => html `
                <button
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-options-button': true,
            'hlx-selected': year === this.displayedYear,
        })}
                    @click=${() => {
            this.displayedYear = year;
            this.viewState = 'selecting_date';
        }}>
                    ${year}
                </button>
            `);
        return html `
            <div class="hlx-options-grid">${yearButtons}</div>
        `;
    }
    renderDateSelector() {
        const firstDayOfDisplayedMonth = new UTCDate(this.displayedYear, this.displayedMonth);
        const daysInMonth = getDaysInMonth(firstDayOfDisplayedMonth);
        const days = [...Array(daysInMonth).keys()].map((dayIndex) => addDays(firstDayOfDisplayedMonth, dayIndex));
        const weekdayLabels = [];
        const firstDayOfWeek = startOfWeek(firstDayOfDisplayedMonth, {
            locale: getDateFnsLocale(),
        });
        for (let i = 0; i < 7; i++) {
            const day = addDays(firstDayOfWeek, i);
            weekdayLabels.push(day
                .toLocaleDateString(localizationConfig.getLocale(), { weekday: 'short' })
                .substring(0, 2));
        }
        const weekdayHeader = weekdayLabels.map((label) => html `
                <div class="hlx-weekday-label">${label}</div>
            `);
        const spacers = new Array(Math.max(firstDayOfDisplayedMonth.getDay() - firstDayOfWeek.getDay(), 0)).fill(html `
            <div />
        `);
        return html `
            <div
                class=${classMap({
            'hlx-month': true,
        })}>
                ${weekdayHeader}${spacers}${this.renderDays(days)}
            </div>
        `;
    }
    renderDays(days) {
        return days.map((day) => html `
                <button
                    ?disabled=${(this.min && isBefore(day, this.min)) ||
            (this.max && isAfter(day, this.max))}
                    class=${classMap({
            'hlx-date-picker-button': true,
            'hlx-day': true,
            'hlx-selected': this.value && isSameDay(day, this.value),
            'hlx-start-of-range-day': this.isStartOfSelectedRange(day),
            'hlx-in-range-day': this.isInSelectedRange(day),
            'hlx-end-of-range-day': this.isEndOfSelectedRange(day),
        })}
                    @click=${() => this.handleDateClick(day)}>
                    ${day.getDate()}
                </button>
            `);
    }
    get validity() {
        this.validateInput();
        return this.input.validity;
    }
    get validationMessage() {
        return this.input.validationMessage;
    }
    checkValidity() {
        this.validateInput();
        return this.input?.checkValidity() ?? true;
    }
    reportValidity() {
        this.validateInput();
        return this.input?.reportValidity() ?? true;
    }
    validateInput() {
        if (!this.input?.value) {
            if (this.required) {
                this.setCustomValidity(msg('This field is required'));
                return;
            }
            else {
                this.setCustomValidity('');
                return;
            }
        }
        if (this.input?.value && !this.value) {
            this.setCustomValidity(msg(str `Invalid date format, please use ${this.getLocalizedDateFormat()}`));
            return;
        }
        if (this.min && isBefore(this.value, this.min)) {
            this.setCustomValidity(msg(str `Select a date starting from ${format(this.min, 'P', { locale: getDateFnsLocale() })}`));
            return;
        }
        if (this.max && isAfter(this.value, this.max)) {
            this.setCustomValidity(msg(str `Select a date up to ${format(this.max, 'P', { locale: getDateFnsLocale() })}`));
            return;
        }
        this.setCustomValidity('');
    }
    setCustomValidity(message) {
        this.input?.setCustomValidity(message);
    }
    getForm() {
        return findForm(this);
    }
    handleInput() {
        const parsedDate = parse(this.input.value, 'P', new Date(), {
            locale: getDateFnsLocale(),
        });
        // `>= 1000` checks that all four digits of the year have been entered,
        // otherwise values like 10/10/24 are interpreted as October 10th, 0024
        if (isValid(parsedDate) && parsedDate.getFullYear() >= 1000) {
            this.value = new UTCDate(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
            this.displayedYear = this.value.getFullYear();
            this.displayedMonth = this.value.getMonth();
        }
        else {
            this.value = undefined;
        }
        this.emit('hlx-input');
    }
    handleDateClick(date) {
        this.value = date;
        this.input.value = format(date, 'P', { locale: getDateFnsLocale() });
        this.emit('hlx-input');
    }
    handleBlur() {
        this.viewState = 'hidden';
    }
    togglePopup() {
        this.toggleViewState('hidden', 'selecting_date');
    }
    toggleSelectingMonthView() {
        this.toggleViewState('selecting_date', 'selecting_month');
    }
    toggleSelectingYearView() {
        this.toggleViewState('selecting_date', 'selecting_year');
    }
    toggleViewState(defaultState, toggledState) {
        if (this.viewState === toggledState) {
            this.viewState = defaultState;
        }
        else {
            this.viewState = toggledState;
        }
    }
    updateDisplayedMonth() {
        const dateToDisplay = this.getDateToDisplay();
        this.displayedYear = dateToDisplay.getFullYear();
        this.displayedMonth = dateToDisplay.getMonth();
    }
    handleDefaultValueUpdate() {
        if (this.defaultValue && !this.value && !this.input?.value) {
            this.value = this.defaultValue;
            if (this.input) {
                this.input.value = format(this.value, 'P', {
                    locale: getDateFnsLocale(),
                });
            }
        }
    }
    incrementDisplayedMonth() {
        if (this.displayedMonth >= 11) {
            this.displayedMonth = 0;
            this.displayedYear += 1;
            return;
        }
        this.displayedMonth += 1;
    }
    decrementDisplayedMonth() {
        if (this.displayedMonth <= 0) {
            this.displayedMonth = 11;
            this.displayedYear -= 1;
            return;
        }
        this.displayedMonth -= 1;
    }
    getDateToDisplay() {
        if (this.value) {
            return this.value;
        }
        const currentDate = new Date();
        if (this.max && isAfter(currentDate, this.max)) {
            return this.max;
        }
        if (this.min && isBefore(currentDate, this.min)) {
            return this.min;
        }
        return currentDate;
    }
    isStartOfSelectedRange(day) {
        return ((this.selectedFrom && isSameDay(day, this.selectedFrom)) ||
            (this.type === 'range_from' && this.value && isSameDay(day, this.value)));
    }
    isInSelectedRange(day) {
        if (this.type !== 'range_to' && this.type !== 'range_from') {
            return false;
        }
        const from = this.type === 'range_from' ? this.value : this.selectedFrom;
        const to = this.type === 'range_to' ? this.value : this.selectedTo;
        return from && to && isAfter(day, from) && isBefore(day, to);
    }
    isEndOfSelectedRange(day) {
        return ((this.selectedTo && isSameDay(day, this.selectedTo)) ||
            (this.type === 'range_to' && this.value && isSameDay(day, this.value)));
    }
    getLocalizedDateFormat() {
        const date = new UTCDate(1345, 11, 31);
        return format(date, 'P', { locale: getDateFnsLocale() })
            .replace('1345', msg('YYYY'))
            .replace('12', msg('MM'))
            .replace('31', msg('DD'));
    }
};
HlxDatePicker.styles = [
    componentStyles,
    formControlStyles,
    css `
            .hlx-date-picker-container {
                display: block;
                opacity: 0;
                padding: var(--hlx-spacing-xl) var(--hlx-spacing-lg);
                box-sizing: content-box;
                width: 280px;
                z-index: 1070;
                border: 1px solid var(--hlx-border-color-default);
                border-radius: var(--hlx-radius-xs);
                background: var(--hlx-background-color-default);
            }

            .hlx-date-picker-container[data-hide] {
                opacity: 0;
                display: none;
                transition: display 0 var(--hlx-tooltip-animation-duration);
                animation: fadeout var(--hlx-tooltip-animation-duration) linear;
            }

            .hlx-date-picker-container[data-show] {
                opacity: 1;
                display: block;
                animation: fadein var(--hlx-tooltip-animation-duration) linear;
            }
            .hlx-open-date-picker-button {
                height: 100%;
                width: 56px;
                color: var(--hlx-button-color-tertiary-brand-default);
            }
            .hlx-open-date-picker-button:disabled {
                color: var(--hlx-button-color-tertiary-disabled);
            }

            .hlx-date-picker-button {
                background: none;
                border: none;
                padding: 0;
                cursor: pointer;
                border-radius: var(--hlx-radius-sm);
                font-size: var(--hlx-text-font-size-md);
                font-family: var(--hlx-text-font-family-sans-serif);
            }

            .hlx-date-picker-button.hlx-selected {
                background: var(--hlx-color-lavender-50);
                color: var(--hlx-text-color-inverted-default);
                font-weight: var(--hlx-text-font-weight-semibold);
            }

            .hlx-date-picker-button.hlx-dropdown {
                font-weight: var(--hlx-text-font-weight-semibold);
                height: 100%;
            }

            .hlx-date-picker-header {
                display: flex;
                justify-content: space-between;
                padding: var(--hlx-spacing-xs);
            }

            .hlx-month-navigation-button {
                width: 24px;
                height: 100%;
                color: var(--hlx-text-link-color-default);
            }
            .hlx-month-navigation-button:active {
                color: var(--hlx-text-link-color-active);
            }
            .hlx-month-navigation-button:hover {
                color: var(--hlx-text-link-color-hover);
            }
            .hlx-month-navigation-button:disabled {
                color: var(--hlx-button-color-tertiary-disabled);
            }
            .hlx-month-navigation-button:first-child {
                margin-right: 12px;
            }

            .hlx-options-grid {
                display: grid;
                grid-template-columns: repeat(4, auto);
            }
            .hlx-options-button {
                width: 76px;
                height: var(--hlx-spacing-2xl);
            }

            .hlx-month {
                width: fit-content;
                display: grid;
                grid-template-columns: repeat(7, auto);
                row-gap: 8px;
            }

            .hlx-day {
                width: 40px;
                height: 40px;
                text-align: center;
            }
            .hlx-weekday-label {
                display: flex;
                width: 40px;
                height: 40px;
                justify-content: center;
                align-items: center;
                color: var(--hlx-text-color-weak-2);
                font-size: var(--hlx-text-font-size-md);
                font-family: var(--hlx-text-font-family-sans-serif);
            }
            .hlx-start-of-range-day {
                background: var(--hlx-color-lavender-50);
                color: var(--hlx-text-color-inverted-default);
                font-weight: var(--hlx-text-font-weight-semibold);
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }
            .hlx-in-range-day {
                background: var(--hlx-color-lavender-10);
                color: var(--hlx-text-link-color-default);
                font-weight: var(--hlx-text-font-weight-semibold);
                border-radius: 0;
            }
            .hlx-end-of-range-day {
                background: var(--hlx-color-lavender-50);
                color: var(--hlx-text-color-inverted-default);
                font-weight: var(--hlx-text-font-weight-semibold);
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        `,
];
__decorate([
    state()
], HlxDatePicker.prototype, "viewState", void 0);
__decorate([
    state()
], HlxDatePicker.prototype, "displayedMonth", void 0);
__decorate([
    state()
], HlxDatePicker.prototype, "displayedYear", void 0);
__decorate([
    property({ type: Date })
], HlxDatePicker.prototype, "value", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxDatePicker.prototype, "type", void 0);
__decorate([
    property({ type: Date, converter: toUtcDate, attribute: 'default-value' })
], HlxDatePicker.prototype, "defaultValue", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxDatePicker.prototype, "disabled", void 0);
__decorate([
    property({ type: Date, converter: toUtcDate })
], HlxDatePicker.prototype, "max", void 0);
__decorate([
    property({ type: Date, converter: toUtcDate })
], HlxDatePicker.prototype, "min", void 0);
__decorate([
    property({ type: String })
], HlxDatePicker.prototype, "name", void 0);
__decorate([
    property({ type: Date, converter: toUtcDate, attribute: 'selected-from' })
], HlxDatePicker.prototype, "selectedFrom", void 0);
__decorate([
    property({ type: Date, converter: toUtcDate, attribute: 'selected-to' })
], HlxDatePicker.prototype, "selectedTo", void 0);
__decorate([
    property({ type: String })
], HlxDatePicker.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], HlxDatePicker.prototype, "required", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxDatePicker.prototype, "helpText", void 0);
__decorate([
    property({ type: String })
], HlxDatePicker.prototype, "label", void 0);
__decorate([
    query('.hlx-date-picker-control')
], HlxDatePicker.prototype, "input", void 0);
__decorate([
    query('.hlx-date-picker-container')
], HlxDatePicker.prototype, "datePickerContainer", void 0);
__decorate([
    watch('viewState')
], HlxDatePicker.prototype, "handleViewStateChange", null);
__decorate([
    watch('value'),
    watch('min'),
    watch('max')
], HlxDatePicker.prototype, "updateDisplayedMonth", null);
__decorate([
    watch('defaultValue')
], HlxDatePicker.prototype, "handleDefaultValueUpdate", null);
HlxDatePicker = __decorate([
    customElement('hlx-date-picker'),
    localized()
    // TODO implementation of these interfaces is not actually correct, e.g. validation controller
    // is not used
], HlxDatePicker);
export { HlxDatePicker };
function toUtcDate(input) {
    if (!input) {
        return undefined;
    }
    const localDate = new Date(input);
    return new UTCDate(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());
}
function getDateFnsLocale() {
    const currentLocaleCode = localizationConfig.getLocale();
    return DateFnsLocales[currentLocaleCode] ?? DateFnsLocales.enUS;
}
//# sourceMappingURL=date-picker.js.map